import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { noop } from "../../utils/noop";
import { isFunction } from "../../utils/is-function";
import { mergePropsAndRefs } from "../../utils";
import { MenuItem } from "./MenuItem";
import { menuLabelCss } from "./MenuLabel";
import { Text } from "../Text";
import { theme } from "../../theme";
import { Shortcut } from "./Shortcut";
import { IconChevronRight } from "@frameio/vapor-icons/build/esm/icons/IconChevronRight";
var IconBefore = styled.div.withConfig({
  displayName: "MenuButton__IconBefore",
  componentId: "vapor__sc-1go0haz-0"
})(["display:flex;align-items:center;"]);
var IconAfter = styled.div.withConfig({
  displayName: "MenuButton__IconAfter",
  componentId: "vapor__sc-1go0haz-1"
})(["margin-left:", ";margin-right:0;display:flex;align-items:center;margin-right:", ";", "{margin-right:-", ";}"], theme.spaces[12], theme.spaces[4], Shortcut, theme.spaces[4]);
var Details = Text;
var TextContent = styled.div.withConfig({
  displayName: "MenuButton__TextContent",
  componentId: "vapor__sc-1go0haz-2"
})(["display:flex;flex-direction:column;justify-content:center;min-width:0;flex:1;margin-left:", ";margin-right:", ";"], theme.spaces[4], theme.spaces[4]);
var StyledMenuLabel = styled.div.withConfig({
  displayName: "MenuButton__StyledMenuLabel",
  componentId: "vapor__sc-1go0haz-3"
})(["", ";cursor:", ";min-height:calc(", " + ", ");&[aria-haspopup='menu']{cursor:default;}", ",", "{color:", ";}", ",", "{height:", ";}", "{padding:", ";}&[disabled]{opacity:0.3;pointer-events:none;}"], menuLabelCss, function (p) {
  return p.$interactive ? 'pointer' : 'auto';
}, theme.spaces[32], theme.spaces[2], IconBefore, IconAfter, theme.colors.icon.primary, IconBefore, IconAfter, theme.spaces[32], TextContent, function (p) {
  return p.hasDetails ? '5px 0' : 0;
});
/**
 * MenuButton
 *
 * The primary component for rendering selectable menu
 * items in a Menu component.
 */

export var MenuButton = React.forwardRef(function (_ref, forwardedRef) {
  var children = _ref.children,
      submenu = _ref.submenu,
      iconBefore = _ref.iconBefore,
      iconAfter = _ref.iconAfter,
      details = _ref.details,
      destructive = _ref.destructive,
      _ref$interactive = _ref.interactive,
      interactive = _ref$interactive === void 0 ? true : _ref$interactive,
      _ref$onSelect = _ref.onSelect,
      onSelect = _ref$onSelect === void 0 ? noop : _ref$onSelect,
      _ref$role = _ref.role,
      role = _ref$role === void 0 ? 'menuitem' : _ref$role,
      _ref$shouldCloseMenuO = _ref.shouldCloseMenuOnSelect,
      shouldCloseMenuOnSelect = _ref$shouldCloseMenuO === void 0 ? true : _ref$shouldCloseMenuO,
      _ref$delayBeforeClosi = _ref.delayBeforeClosingMenuUponSelection,
      delayBeforeClosingMenuUponSelection = _ref$delayBeforeClosi === void 0 ? 0 : _ref$delayBeforeClosi,
      isLink = _ref.isLink,
      onClickProp = _ref.onClick,
      other = _objectWithoutProperties(_ref, ["children", "submenu", "iconBefore", "iconAfter", "details", "destructive", "interactive", "onSelect", "role", "shouldCloseMenuOnSelect", "delayBeforeClosingMenuUponSelection", "isLink", "onClick"]);

  var renderIconAfter = useCallback(function (_ref2) {
    var isSelected = _ref2.isSelected;

    if (submenu) {
      return /*#__PURE__*/React.createElement(IconAfter, null, /*#__PURE__*/React.createElement(IconChevronRight, null));
    }

    if (iconAfter) {
      return /*#__PURE__*/React.createElement(IconAfter, null, typeof iconAfter === 'function' ? iconAfter({
        isSelected: isSelected
      }) : iconAfter);
    }

    return null;
  }, [iconAfter, submenu]);
  var renderIconBefore = useCallback(function (_ref3) {
    var isSelected = _ref3.isSelected;

    if (iconBefore) {
      return /*#__PURE__*/React.createElement(IconBefore, null, isFunction(iconBefore) ? iconBefore({
        isSelected: isSelected
      }) : iconBefore);
    }

    return null;
  }, [iconBefore]);
  return /*#__PURE__*/React.createElement(MenuItem, {
    delayBeforeClosingMenuUponSelection: delayBeforeClosingMenuUponSelection,
    shouldCloseMenuOnSelect: shouldCloseMenuOnSelect,
    onSelect: onSelect,
    submenu: submenu,
    isLink: isLink,
    onClick: onClickProp
  }, function (_ref4) {
    var isSelected = _ref4.isSelected,
        isExpanded = _ref4.isExpanded,
        bind = _ref4.bind;
    return /*#__PURE__*/React.createElement(StyledMenuLabel, _extends({
      $interactive: interactive,
      role: role,
      "data-destructive": destructive,
      hasDetails: !!details
    }, mergePropsAndRefs(other, bind, {
      ref: forwardedRef
    })), /*#__PURE__*/React.createElement(HoverContainer, null, renderIconBefore({
      isSelected: isSelected
    }), /*#__PURE__*/React.createElement(TextContent, null, /*#__PURE__*/React.createElement(Text, {
      variant: "body",
      color: "body",
      truncate: true
    }, isFunction(children) ? children({
      isSelected: isSelected,
      isExpanded: isExpanded
    }) : children), details && /*#__PURE__*/React.createElement(Details, {
      color: "tertiary",
      variant: "secondary"
    }, details)), renderIconAfter({
      isSelected: isSelected
    })));
  });
});
export var testExports = {
  StyledMenuLabel: StyledMenuLabel
};
var HoverContainer = styled.div.withConfig({
  displayName: "MenuButton__HoverContainer",
  componentId: "vapor__sc-1go0haz-4"
})(["display:flex;align-items:center;justify-content:center;position:relative;flex:1;max-width:100%;"]);